import React, {useState} from "react";
import {DateTime} from "luxon";
import {useSelector} from "react-redux";
import {RootState} from "redux/store";

import Box from "@mui/material/Box";
import {useTheme} from "@mui/material/styles";

import {
    GanttBarDragItem,
    GanttOLDragItem,
    GANTT_OL_TYPE_PREFIX,
} from "./GanttBar";
import DnDDroppableBox from "../../../../../../common/DnD/DnDDroppableBox";
import {GiornoLavorativo} from "components/anagrafiche/anagraficheSlice";

// ============================================
// INTERFACES
// ============================================

interface GanttDropCellProps {
    macchinaId: number;
    giorno: DateTime;
    acceptedTypes: string[];
    onDrop: (draggedItem: GanttBarDragItem) => void;
    onResize: (draggedItem: GanttBarDragItem, nuovaData: string) => void;
    onOrdineLavoroDrop: (ordineLavoroId: number, macchinaId: number, data: string) => void;
    minHeight?: number;
}

// ============================================
// COMPONENT
// ============================================

const GanttDropCell = ({
                           macchinaId,
                           giorno,
                           acceptedTypes,
                           onDrop,
                           onResize,
                           onOrdineLavoroDrop,
                           minHeight = 60,
                       }: GanttDropCellProps) => {
    const theme = useTheme();
    const [isOver, setIsOver] = useState(false);

    const dataISO = giorno.toISODate() || "";
    const giorniLavorativi = useSelector<RootState, Record<string, GiornoLavorativo>>(
        (state) => state.anagraficheState?.giorniLavorativi?.giorni || {}
    );
    const giornoInfo = giorniLavorativi[dataISO];
    const isLavorativo = giornoInfo ? giornoInfo.lavorativo : giorno.weekday < 6;
    const isOggi = giorno.hasSame(DateTime.now(), "day");

    // Background in base allo stato
    const getBackgroundColor = () => {
        if (isOver) {
            return theme.palette.success.light + "80";
        }
        if (isOggi) {
            return theme.palette.primary.light + "40";
        }
        if (!isLavorativo) {
            return theme.palette.grey[200];
        }
        return "transparent";
    };

    const handleDrop = (draggedItem: GanttBarDragItem | GanttOLDragItem) => {
        setIsOver(false);

        const nuovaData = giorno.toFormat("yyyy-MM-dd");

        // Verifica se è un drop di OdL (nuovo scheduling)
        if (draggedItem.type.startsWith(GANTT_OL_TYPE_PREFIX)) {
            const olItem = draggedItem as GanttOLDragItem;
            console.log("=== DROP ORDINE LAVORO (NUOVO SCHEDULING) ===", {
                ordineLavoroId: olItem.ordineLavoroId,
                macchinaId,
                data: nuovaData,
                tecnicaId: olItem.tecnicaId,
                valoreConfermato: olItem.valoreConfermato,
            });
            onOrdineLavoroDrop(olItem.ordineLavoroId, macchinaId, nuovaData);
            return;
        }

        // È un drag di barra esistente
        const barItem = draggedItem as GanttBarDragItem;

        // Verifica se è un resize o uno spostamento
        if (barItem.resizeEdge) {
            console.log("=== RESIZE ===", {
                assegnazioneId: barItem.assegnazioneId,
                edge: barItem.resizeEdge,
                nuovaData,
                dataDaOriginale: barItem.dataDa,
                dataAOriginale: barItem.dataA,
            });
            onResize(barItem, nuovaData);
        } else {
            console.log("=== DROP (MOVE) ===", {
                assegnazioneId: barItem.assegnazioneId,
                nuovaMacchinaId: macchinaId,
                nuovaData,
                dataDaOriginale: barItem.dataDa,
                dataAOriginale: barItem.dataA,
            });
            onDrop(barItem);
        }
    };

    return (
        <DnDDroppableBox
            value={{macchinaId, data: giorno.toISODate()}}
            accept={acceptedTypes}
            showDropIndicator={false}
            onDragEnter={() => setIsOver(true)}
            onDragLeave={() => setIsOver(false)}
            onDrop={handleDrop}
        >
            <Box
                sx={{
                    borderRight: `1px solid ${theme.palette.divider}`,
                    backgroundColor: getBackgroundColor(),
                    minHeight,
                    flex: 1,
                    transition: "background-color 0.15s ease",
                }}
            />
        </DnDDroppableBox>
    );
};

export default GanttDropCell;